import { useRef, useState, useEffect, useCallback } from 'react';
import type { TracedFace, TracedObstruction } from '../types';
import type { ImageryResult } from '../lib/satellite';
import { generateFaceId, generateObstructionId } from '../lib/roofExtrusion';

// ============================================================
// ROOF TRACER — 2D canvas overlay for tracing roof outlines
// and obstructions from satellite imagery
// ============================================================

interface RoofTracerProps {
  imagery: ImageryResult;
  onFacesComplete: (faces: TracedFace[], obstructions: TracedObstruction[]) => void;
  onCancel: () => void;
}

type TraceTool = 'roof' | 'obstruction';
type ObstructionType = 'tree' | 'chimney' | 'vent';

// Convert feet coordinates to canvas pixels
function feetToCanvas(
  xFeet: number,
  yFeet: number,
  imagery: ImageryResult,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number } {
  const imgLeft = imagery.centerOffsetX - imagery.widthFeet;
  const imgTop = imagery.centerOffsetY - imagery.heightFeet;
  const x = ((xFeet - imgLeft) / imagery.widthFeet) * canvasWidth;
  const y = ((yFeet - imgTop) / imagery.heightFeet) * canvasHeight;
  return { x, y };
}

// Convert canvas pixels to feet coordinates
function canvasToFeet(
  xCanvas: number,
  yCanvas: number,
  imagery: ImageryResult,
  canvasWidth: number,
  canvasHeight: number
): [number, number] {
  const imgLeft = imagery.centerOffsetX - imagery.widthFeet;
  const imgTop = imagery.centerOffsetY - imagery.heightFeet;
  const xFeet = imgLeft + (xCanvas / canvasWidth) * imagery.widthFeet;
  const yFeet = imgTop + (yCanvas / canvasHeight) * imagery.heightFeet;
  return [xFeet, yFeet];
}

export function RoofTracer({ imagery, onFacesComplete, onCancel }: RoofTracerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [faces, setFaces] = useState<TracedFace[]>([]);
  const [obstructions, setObstructions] = useState<TracedObstruction[]>([]);
  const [currentFace, setCurrentFace] = useState<TracedFace | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [pitchInput, setPitchInput] = useState(25);
  const [heightInput, setHeightInput] = useState(18);
  const [labelInput, setLabelInput] = useState('');
  const [phase, setPhase] = useState<'tracing' | 'pitch_input'>('tracing');
  const [canvasSize, setCanvasSize] = useState({ width: 768, height: 768 });
  const [tool, setTool] = useState<TraceTool>('roof');
  const [obsType, setObsType] = useState<ObstructionType>('tree');
  const [obsSize, setObsSize] = useState(12); // feet diameter
  const [obsHeight, setObsHeight] = useState(25); // feet height

  // Load the satellite image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      draw();
    };
    img.src = imagery.dataUrl;
  }, [imagery]);

  // Resize canvas to fit container
  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const aspectRatio = imagery.widthFeet / imagery.heightFeet;

      let w, h;
      if (containerWidth / containerHeight > aspectRatio) {
        h = containerHeight;
        w = h * aspectRatio;
      } else {
        w = containerWidth;
        h = w / aspectRatio;
      }
      setCanvasSize({ width: Math.round(w), height: Math.round(h) });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [imagery]);

  // Draw everything
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = canvasSize;
    canvas.width = width;
    canvas.height = height;

    // Draw satellite image
    if (imgRef.current) {
      ctx.drawImage(imgRef.current, 0, 0, width, height);
    }

    // Semi-transparent overlay
    ctx.fillStyle = 'rgba(10, 14, 23, 0.25)';
    ctx.fillRect(0, 0, width, height);

    // Draw obstructions
    obstructions.forEach(obs => {
      drawObstruction(ctx, obs, width, height);
    });

    // Draw completed faces
    faces.forEach((face, idx) => {
      drawFace(ctx, face, width, height, `hsl(${idx * 60}, 70%, 50%)`);
    });

    // Draw current face being traced
    if (currentFace && currentFace.points.length > 0) {
      drawFace(ctx, currentFace, width, height, '#f59e0b', mousePos);
    }

    // Draw obstruction preview when in obstruction mode
    if (tool === 'obstruction' && mousePos) {
      const previewObs: TracedObstruction = {
        id: 'preview',
        type: obsType,
        position: canvasToFeet(mousePos.x, mousePos.y, imagery, width, height),
        width: obsSize,
        height: obsHeight,
      };
      ctx.globalAlpha = 0.5;
      drawObstruction(ctx, previewObs, width, height);
      ctx.globalAlpha = 1.0;
    }

    // Draw property center marker
    const center = feetToCanvas(0, 0, imagery, width, height);
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(center.x, center.y, 6, 0, Math.PI * 2);
    ctx.stroke();
  }, [canvasSize, faces, obstructions, currentFace, mousePos, imagery, tool, obsType, obsSize, obsHeight]);

  useEffect(() => {
    draw();
  }, [draw]);

  function drawFace(
    ctx: CanvasRenderingContext2D,
    face: TracedFace,
    canvasW: number,
    canvasH: number,
    color: string,
    mouse?: { x: number; y: number } | null
  ) {
    if (face.points.length === 0) return;

    const pts = face.points.map(p => feetToCanvas(p[0], p[1], imagery, canvasW, canvasH));

    if (face.closed) {
      ctx.fillStyle = color + '20';
      ctx.beginPath();
      pts.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.fill();
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    pts.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });

    if (!face.closed && mouse && pts.length > 0) {
      ctx.lineTo(mouse.x, mouse.y);
    }

    if (face.closed) {
      ctx.closePath();
    }
    ctx.stroke();

    // Draw nodes
    pts.forEach(p => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    if (face.closed && face.label) {
      const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
      const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(face.label, cx, cy);
      ctx.textAlign = 'left';
    }
  }

  function drawObstruction(
    ctx: CanvasRenderingContext2D,
    obs: TracedObstruction,
    canvasW: number,
    canvasH: number
  ) {
    const pos = feetToCanvas(obs.position[0], obs.position[1], imagery, canvasW, canvasH);
    const radiusFeet = obs.width / 2;
    const radiusCanvas = (radiusFeet / imagery.widthFeet) * canvasW;

    // Color by type
    const colors: Record<TracedObstruction['type'], string> = {
      tree: '#22c55e',
      chimney: '#ef4444',
      vent: '#f59e0b',
      other: '#94a3b8',
    };
    const color = colors[obs.type];

    // Draw filled circle
    ctx.fillStyle = color + '40';
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, radiusCanvas, 0, Math.PI * 2);
    ctx.fill();

    // Draw border
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw icon based on type
    ctx.fillStyle = color;
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const icons: Record<TracedObstruction['type'], string> = { tree: '🌳', chimney: '🏠', vent: '⭕', other: '❓' };
    ctx.fillText(icons[obs.type], pos.x, pos.y);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  // Handle canvas clicks
  function handleCanvasClick(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const xCanvas = ((e.clientX - rect.left) / rect.width) * canvasSize.width;
    const yCanvas = ((e.clientY - rect.top) / rect.height) * canvasSize.height;
    const [xFeet, yFeet] = canvasToFeet(xCanvas, yCanvas, imagery, canvasSize.width, canvasSize.height);

    if (tool === 'obstruction') {
      // Add an obstruction at click position
      const newObs: TracedObstruction = {
        id: generateObstructionId(),
        type: obsType,
        position: [xFeet, yFeet],
        width: obsSize,
        height: obsHeight,
      };
      setObstructions([...obstructions, newObs]);
      return;
    }

    // Roof tracing mode
    if (phase !== 'tracing') return;

    if (!currentFace) {
      const newFace: TracedFace = {
        id: generateFaceId(),
        label: '',
        points: [[xFeet, yFeet]],
        pitch: 25,
        height: 18,
        closed: false,
      };
      setCurrentFace(newFace);
    } else {
      const firstPoint = currentFace.points[0];
      const firstCanvas = feetToCanvas(firstPoint[0], firstPoint[1], imagery, canvasSize.width, canvasSize.height);
      const dist = Math.sqrt(
        Math.pow(xCanvas - firstCanvas.x, 2) + Math.pow(yCanvas - firstCanvas.y, 2)
      );

      if (dist < 15 && currentFace.points.length >= 3) {
        const closedFace = { ...currentFace, closed: true };
        setCurrentFace(closedFace);
        setPhase('pitch_input');
        return;
      }

      setCurrentFace({
        ...currentFace,
        points: [...currentFace.points, [xFeet, yFeet]],
      });
    }
  }

  function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * canvasSize.width;
    const y = ((e.clientY - rect.top) / rect.height) * canvasSize.height;
    setMousePos({ x, y });
  }

  function handleMouseLeave() {
    setMousePos(null);
  }

  function handleUndo() {
    if (tool === 'obstruction') {
      setObstructions(obstructions.slice(0, -1));
      return;
    }
    if (!currentFace) return;
    if (currentFace.points.length > 1) {
      setCurrentFace({ ...currentFace, points: currentFace.points.slice(0, -1), closed: false });
    } else {
      setCurrentFace(null);
    }
    setPhase('tracing');
  }

  function handleCancelFace() {
    setCurrentFace(null);
    setPhase('tracing');
  }

  function handleConfirmFace() {
    if (!currentFace || !currentFace.closed) return;
    const finalFace: TracedFace = {
      ...currentFace,
      pitch: pitchInput,
      height: heightInput,
      label: labelInput || `Face ${faces.length + 1}`,
    };
    setFaces([...faces, finalFace]);
    setCurrentFace(null);
    setLabelInput('');
    setPhase('tracing');
  }

  function handleDeleteFace(id: string) {
    setFaces(faces.filter(f => f.id !== id));
  }

  function handleDeleteObstruction(id: string) {
    setObstructions(obstructions.filter(o => o.id !== id));
  }

  function handleFinish() {
    onFacesComplete(faces, obstructions);
  }

  function handleUseSimulated() {
    onFacesComplete([], []);
  }

  return (
    <div className="roof-tracer-overlay">
      {/* Toolbar */}
      <div className="tracer-toolbar">
        <div className="tracer-title">🏗️ Roof & Obstruction Tracing Tool</div>

        {/* Tool selector */}
        <div className="tracer-tool-selector">
          <button
            className={`tracer-tool-btn ${tool === 'roof' ? 'active' : ''}`}
            onClick={() => { setTool('roof'); setPhase('tracing'); setCurrentFace(null); }}
          >
            🏠 Roof Faces
          </button>
          <button
            className={`tracer-tool-btn ${tool === 'obstruction' ? 'active' : ''}`}
            onClick={() => { setTool('obstruction'); setCurrentFace(null); setPhase('tracing'); }}
          >
            🌳 Obstructions
          </button>
        </div>

        <div className="tracer-instructions">
          {tool === 'roof' && phase === 'tracing' && !currentFace && (
            <span>Click on the satellite image to start tracing a roof face</span>
          )}
          {tool === 'roof' && phase === 'tracing' && currentFace && !currentFace.closed && (
            <span>Click to add nodes. Click the first node to close. ({currentFace.points.length} nodes)</span>
          )}
          {tool === 'roof' && phase === 'pitch_input' && (
            <span>Set pitch and height, then click Confirm Face</span>
          )}
          {tool === 'obstruction' && (
            <span>Click to place a {obsType}. Adjust size and height below.</span>
          )}
        </div>

        {/* Obstruction controls */}
        {tool === 'obstruction' && (
          <div className="obs-controls">
            <label>
              Type:
              <select value={obsType} onChange={e => setObsType(e.target.value as ObstructionType)}>
                <option value="tree">🌳 Tree</option>
                <option value="chimney">🏠 Chimney</option>
                <option value="vent">⭕ Vent</option>
              </select>
            </label>
            <label>
              Width:
              <input type="number" value={obsSize} onChange={e => setObsSize(parseFloat(e.target.value) || 10)} min={1} max={40} />ft
            </label>
            <label>
              Height:
              <input type="number" value={obsHeight} onChange={e => setObsHeight(parseFloat(e.target.value) || 15)} min={1} max={60} />ft
            </label>
          </div>
        )}

        <div className="tracer-actions">
          {tool === 'roof' && phase === 'tracing' && currentFace && (
            <>
              <button className="tracer-btn" onClick={handleUndo}>⤺ Undo</button>
              <button className="tracer-btn tracer-btn-danger" onClick={handleCancelFace}>✕ Cancel Face</button>
            </>
          )}
          {tool === 'obstruction' && obstructions.length > 0 && (
            <button className="tracer-btn" onClick={handleUndo}>⤺ Undo Last</button>
          )}
          {phase === 'pitch_input' && (
            <div className="pitch-input-row">
              <label>
                Pitch:
                <input type="number" value={pitchInput} onChange={e => setPitchInput(parseFloat(e.target.value) || 0)} min={0} max={45} />°
              </label>
              <label>
                Height:
                <input type="number" value={heightInput} onChange={e => setHeightInput(parseFloat(e.target.value) || 10)} min={5} max={50} />ft
              </label>
              <label>
                Label:
                <input type="text" value={labelInput} onChange={e => setLabelInput(e.target.value)} placeholder="e.g., South" style={{ width: 80 }} />
              </label>
              <button className="tracer-btn tracer-btn-confirm" onClick={handleConfirmFace}>✓ Confirm Face</button>
            </div>
          )}
        </div>

        <div className="tracer-finish-row">
          <span className="tracer-face-count">
            {faces.length} face{faces.length !== 1 ? 's' : ''} • {obstructions.length} obstruction{obstructions.length !== 1 ? 's' : ''}
          </span>
          <button className="tracer-btn tracer-btn-finish" onClick={handleFinish} disabled={faces.length === 0}>
            🚀 Extrude & Design ({faces.length} faces, {obstructions.length} obs)
          </button>
          <button className="tracer-btn tracer-btn-skip" onClick={handleUseSimulated}>Use Simulated Roof</button>
          <button className="tracer-btn tracer-btn-danger" onClick={onCancel}>✕ Exit</button>
        </div>
      </div>

      {/* Face + obstruction list */}
      {(faces.length > 0 || obstructions.length > 0) && (
        <div className="tracer-face-list">
          {faces.map((f, i) => (
            <div key={f.id} className="tracer-face-item">
              <span className="tracer-face-color" style={{ background: `hsl(${i * 60}, 70%, 50%)` }} />
              <span className="tracer-face-label">{f.label}</span>
              <span className="tracer-face-info">{f.points.length}pts • {f.pitch}°</span>
              <button className="tracer-face-delete" onClick={() => handleDeleteFace(f.id)}>✕</button>
            </div>
          ))}
          {obstructions.map((o, i) => (
            <div key={o.id} className="tracer-face-item">
              <span className="tracer-face-color" style={{ background: o.type === 'tree' ? '#22c55e' : o.type === 'chimney' ? '#ef4444' : '#f59e0b' }} />
              <span className="tracer-face-label">{o.type}</span>
              <span className="tracer-face-info">{o.width}ft × {o.height}ft</span>
              <button className="tracer-face-delete" onClick={() => handleDeleteObstruction(o.id)}>✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Canvas */}
      <div ref={containerRef} className="tracer-canvas-container">
        <canvas
          ref={canvasRef}
          className="tracer-canvas"
          style={{ width: canvasSize.width, height: canvasSize.height, cursor: tool === 'obstruction' ? 'copy' : 'crosshair' }}
          onClick={handleCanvasClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      </div>
    </div>
  );
}
